import React, { useState, useCallback, useEffect, useRef } from "react";
import { gql, useQuery } from '@apollo/client';
import {
  LayoutDashboard,
  BarChart3,
  GitBranch,
  Calculator,
  Store,
  Mail,
  CheckSquare,
  Users,
  Briefcase,
  MessageSquare,
  HelpCircle,
  Menu,
  X,
  Inbox,
  DollarSign,
  BellRing,
  IdCard,
  Pencil,
  MessageCircle,
  LayoutGrid,
} from "lucide-react";
import { GEORGE_CONVERSATION_FEATURE } from "../../../constants/succeedoConstants";
import { DEFAULT_COACH_NAME } from "../Conversation/copy";

// Reads the same currentUser fields AppHeader uses; Apollo cache de-dupes.
// Used for partner-mode detection + the partner's own coach_profile.id
// (powers the "Public Profile" link in the partner sidebar).
// coaching_conversation gives the tenant's coach name for the George
// entry; it is null before the conversation starts (or without the flag).
export const SIDEBAR_USER_QUERY = gql`
  query GetSidebarUser {
    currentUser {
      id
      succeedo_persona
      coach_profile {
        id
        is_partner
      }
    }
    company {
      id
      features
    }
    coaching_conversation {
      id
      coach_name
      new_nudge_count
    }
  }
`;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isActive(href, activePaths) {
  if (typeof window === "undefined") return false;
  return [href, ...(activePaths || [])].includes(window.location.pathname);
}

function NavLink({ item, onClick }) {
  const active = isActive(item.href, item.activePaths);
  const Icon = item.icon;

  if (item.comingSoon) {
    return (
      <span
        className="flex items-center gap-3 px-3 py-2 rounded-[8px] text-muted-foreground/30 cursor-default"
        title="Coming soon"
      >
        <Icon className="w-[18px] h-[18px] shrink-0" />
        <span className="text-[14px]">{item.label}</span>
        <span className="ml-auto font-mono text-[9px] uppercase tracking-wide">Soon</span>
      </span>
    );
  }

  return (
    <a
      href={item.href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-[8px] transition-all",
        item.nested && "ml-6 py-1.5",
        active
          ? "bg-[hsl(var(--succeedo-orange))]/10 text-foreground border-l-2 border-[hsl(var(--succeedo-orange))]"
          : "text-muted-foreground/55 hover:bg-muted hover:text-foreground"
      )}
    >
      {!item.nested && <Icon className="w-[18px] h-[18px] shrink-0" />}
      <span className={item.nested ? "text-[13px]" : "text-[14px]"}>{item.label}</span>
      {item.badge > 0 && (
        <span className="ml-auto bg-[hsl(var(--succeedo-orange))] text-white font-mono text-[10px] rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
          {item.badge}
        </span>
      )}
    </a>
  );
}

// Collapsed rail (tablet and desktop): nested entries (the tools under
// Workspace) have no icons, so they hang off their parent as a small menu.
export function groupNested(items = []) {
  const out = [];
  items.forEach((item) => {
    const parent = out[out.length - 1];
    if (item.nested && parent) parent.children = [...(parent.children || []), item];
    else out.push({ ...item });
  });
  return out;
}

const RAIL_ITEM = "relative flex items-center justify-center w-10 h-10 mx-auto rounded-[8px] transition-all";

function RailBadge({ count }) {
  if (!(count > 0)) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute -top-0.5 -right-0.5 bg-[hsl(var(--succeedo-orange))] text-white font-mono text-[9px] leading-none rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}

function railLabel(item) {
  return item.badge > 0 ? `${item.label} (${item.badge} new)` : item.label;
}

function RailLink({ item, onClick }) {
  const Icon = item.icon;
  const active = isActive(item.href, item.activePaths)
    || (item.children || []).some((child) => isActive(child.href, child.activePaths));
  const classes = cn(
    RAIL_ITEM,
    active
      ? "bg-[hsl(var(--succeedo-orange))]/10 text-foreground"
      : "text-muted-foreground/55 hover:bg-muted hover:text-foreground"
  );
  if (item.comingSoon) {
    return (
      <span className={cn(RAIL_ITEM, "text-muted-foreground/30 cursor-default")} title={`${item.label} (coming soon)`} aria-label={`${item.label} (coming soon)`}>
        <Icon className="w-[18px] h-[18px]" />
      </span>
    );
  }
  if (item.children) return <RailMenu item={item} classes={classes} onNavClick={onClick} />;
  return (
    <a href={item.href} onClick={onClick} className={classes} title={item.label} aria-label={railLabel(item)}>
      <Icon className="w-[18px] h-[18px]" />
      <RailBadge count={item.badge} />
    </a>
  );
}

// The parent's icon opens a menu with the parent itself and its nested
// entries. A click, not hover, so it works on touch screens.
function RailMenu({ item, classes, onNavClick }) {
  const Icon = item.icon;
  const [open, setOpen] = useState(false);
  const [top, setTop] = useState(0);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (menuRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Positioned against the sidebar (its backdrop blur makes it the
  // containing block), level with the icon.
  const toggle = () => {
    const button = buttonRef.current;
    const rail = button?.closest("aside");
    if (button && rail) setTop(button.getBoundingClientRect().top - rail.getBoundingClientRect().top);
    setOpen((v) => !v);
  };
  const menuId = `rail-menu-${item.href.replace(/[^a-z0-9]+/gi, "-")}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className={classes}
        title={item.label}
        aria-label={railLabel(item)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
      >
        <Icon className="w-[18px] h-[18px]" />
        <RailBadge count={item.badge} />
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={item.label}
          className="absolute left-[72px] z-[60] min-w-[200px] bg-card border border-border rounded-[12px] shadow-lg py-2"
          style={{ top }}
        >
          {[item, ...item.children].map((entry) => (
            <a
              key={entry.href}
              role="menuitem"
              href={entry.href}
              onClick={onNavClick}
              className={cn(
                "block px-4 py-2 text-[14px] transition-colors",
                isActive(entry.href, entry.activePaths) ? "text-foreground bg-muted" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                entry.nested && "text-[13px] pl-6"
              )}
            >
              {entry.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
}

function NavSection({ label, items, onNavClick, compact }) {
  if (!items || items.length === 0) return null;
  if (compact) {
    return (
      <div className="px-3 mb-4 space-y-1" role="group" aria-label={label}>
        {groupNested(items).map((item) => (
          <RailLink key={item.href} item={item} onClick={onNavClick} />
        ))}
      </div>
    );
  }
  return (
    <div className="px-3 mb-6">
      <div className="font-mono text-[10px] uppercase text-muted-foreground/30 px-3 mb-2 tracking-wide">
        {label}
      </div>
      <div className="space-y-1">
        {items.map((item) => (
          <NavLink key={item.href} item={item} onClick={onNavClick} />
        ))}
      </div>
    </div>
  );
}

function SidebarContent({ sections, planProgress, showPlanProgress, onNavClick, compact = false }) {
  if (compact) {
    return (
      <>
        {sections.map((section, idx) => (
          <React.Fragment key={section.label}>
            <NavSection label={section.label} items={section.items} onNavClick={onNavClick} compact />
            {idx < sections.length - 1 && <div className="h-px bg-border mx-3 mb-4" />}
          </React.Fragment>
        ))}
        <div className="mt-auto border-t border-border pt-4 pb-4">
          <a
            href="/succeedo/help"
            onClick={onNavClick}
            className={cn(RAIL_ITEM, "text-muted-foreground/55 hover:bg-muted hover:text-foreground")}
            title="Help & Support"
            aria-label="Help & Support"
          >
            <HelpCircle className="w-[18px] h-[18px]" />
          </a>
        </div>
      </>
    );
  }
  return (
    <>
      {sections.map((section, idx) => (
        <React.Fragment key={section.label}>
          <NavSection label={section.label} items={section.items} onNavClick={onNavClick} />
          {idx < sections.length - 1 && <div className="h-px bg-border mb-6" />}
        </React.Fragment>
      ))}

      {/* Bottom Section */}
      <div className="mt-auto border-t border-border pt-4 pb-4 px-3">
        {/* 90-Day Plan Progress — owners only. Partners don't have an exit
            timeline; this bar is the seller's plan. */}
        {showPlanProgress && (
          <div className="mb-4 px-3">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/35 mb-2 tracking-wide">
              YOUR 90-DAY PLAN
            </div>
            <div className="h-1 bg-border rounded-full overflow-hidden mb-1">
              <div
                className="h-full bg-gradient-to-r from-[hsl(var(--succeedo-orange))] to-[hsl(var(--succeedo-orange))]/70 transition-all duration-300"
                style={{ width: `${planProgress.percentage}%` }}
              />
            </div>
            <div className="font-mono text-[10px] text-muted-foreground/35 text-right">
              {planProgress.total > 0
                ? `${planProgress.completed} OF ${planProgress.total} COMPLETE`
                : "STARTS WHEN YOU ACCEPT AN ACTION"}
            </div>
          </div>
        )}

        {/* Help & Support */}
        <a
          href="/succeedo/help"
          onClick={onNavClick}
          className="flex items-center gap-3 px-3 py-2 rounded-[8px] text-muted-foreground/55 hover:bg-muted hover:text-foreground transition-all"
        >
          <HelpCircle className="w-[18px] h-[18px]" />
          <span className="text-[14px]">Help & Support</span>
        </a>
      </div>
    </>
  );
}

export default function AppSidebar({
  persona = "SELLER",
  isPartner: isPartnerProp,
  planProgress,
  actionPlanBadge,
  // Tablet and desktop: an icon rail instead of the full sidebar (toggled
  // from the header). The phone drawer always opens in full.
  collapsed = false,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Canonical partner detection — same source AppHeader uses.
  // Apollo cache de-dupes between header + sidebar.
  const { data: userData } = useQuery(SIDEBAR_USER_QUERY, { fetchPolicy: 'cache-first' });
  const coachProfile = userData?.currentUser?.coach_profile;
  const isPartner = typeof isPartnerProp === 'boolean'
    ? isPartnerProp
    : (coachProfile?.is_partner === true);
  const coachProfileId = coachProfile?.id;

  // Partner sidebar: WORK + PROFILE & EARNINGS. No 90-day plan, no
  // owner tools, no MY WORKSPACE — partners don't have an exit
  // timeline or a personal action plan; their "work" is leads and
  // engagements.
  const partnerSections = [
    {
      label: "WORK",
      items: [
        { icon: Inbox, label: "My Leads", href: "/succeedo/partner/dashboard" },
        { icon: Briefcase, label: "My Engagements", href: "/succeedo/partner/engagements" },
        { icon: MessageSquare, label: "Messages", href: "/succeedo/partner/messages" },
      ],
    },
    {
      label: "PROFILE & EARNINGS",
      items: [
        // Public Profile points at the partner's own coach_profile page;
        // hide the link until the id has loaded so we don't render a
        // broken /succeedo/partners/undefined href.
        ...(coachProfileId
          ? [{ icon: IdCard, label: "Public Profile", href: `/succeedo/partners/${coachProfileId}` }]
          : []),
        { icon: Pencil, label: "Edit Profile", href: "/succeedo/partner/profile/edit" },
        { icon: Briefcase, label: "Browse Partners", href: "/succeedo/partners" },
        { icon: DollarSign, label: "Billing", href: "/succeedo/partner/earnings" },
      ],
    },
  ];

  // Owner (seller/buyer/both) sidebar: TOOLS + MY WORKSPACE.
  // Common tools every owner persona sees.
  const commonTools = [
    { icon: LayoutDashboard, label: "Dashboard", href: "/succeedo/home" },
    { icon: BarChart3, label: "Valuation", href: "/succeedo/valuation" },
    { icon: GitBranch, label: "Deal Structure", href: "/succeedo/deal-structure" },
    { icon: Calculator, label: "Financing", href: "/succeedo/financing-calculator" },
  ];

  const dealRoomEntry      = { icon: Store,    label: "Deal Room",      href: "/succeedo/deal-room"   };
  const inquiriesEntry     = { icon: Mail,     label: "Inquiries",      href: "/succeedo/deal-room/inquiries" };
  const marketplaceEntry   = { icon: Store,    label: "Marketplace",    href: "/succeedo/marketplace" };
  const savedSearchesEntry = { icon: BellRing, label: "Saved Searches", href: "/succeedo/marketplace/saved-searches" };

  // Persona-aware composition. Sellers manage their own listings (Deal
  // Room) and review inquiries; buyers browse the marketplace and
  // manage saved searches; 'both' / nil see everything.
  const personaUpper = (persona || "").toString().toUpperCase();
  let tools;
  if (personaUpper === "SELLER") {
    tools = [...commonTools, dealRoomEntry, inquiriesEntry];
  } else if (personaUpper === "BUYER") {
    tools = [...commonTools, marketplaceEntry, savedSearchesEntry];
  } else {
    tools = [...commonTools, dealRoomEntry, inquiriesEntry, marketplaceEntry, savedSearchesEntry];
  }

  // "Find a Partner" replaces "Partner Directory" — verb form
  // disambiguates from "Your Team" and reads as the action it actually
  // is. "Your Team" replaces "My Deal Team" — the persistent advisory
  // roster is the seat block on the dashboard, and this link goes to
  // the deeper team-workspace view at /succeedo/teams.
  const workspace = [
    { icon: CheckSquare, label: "My Action Plan", href: "/succeedo/action-plan", badge: actionPlanBadge },
    { icon: Users, label: "Your Team", href: "/succeedo/teams" },
    { icon: Briefcase, label: "Find a Partner", href: "/succeedo/partners" },
    { icon: MessageSquare, label: "Messages", href: "/succeedo/messages" },
  ];

  const ownerSections = [
    { label: "TOOLS", items: tools },
    { label: "MY WORKSPACE", items: workspace },
  ];

  // George flag (P1-10): the conversation comes first, the Workspace holds
  // the tools, then the plan; partners and messages below. Buyers keep the
  // tools-first sidebar, matching their dashboard.
  const georgeEnabled = (userData?.company?.features || []).includes(GEORGE_CONVERSATION_FEATURE);
  const coachName = userData?.coaching_conversation?.coach_name || DEFAULT_COACH_NAME;
  const effectivePersona = (userData?.currentUser?.succeedo_persona || persona || "").toString().toUpperCase();
  const nested = (item) => ({ ...item, nested: true });
  const georgeSections = [
    {
      label: "YOUR SUCCESSION",
      items: [
        // Badge: nudges (overdue actions, accepted introductions) since the
        // owner last wrote; it clears when they reply.
        {
          icon: MessageCircle,
          label: coachName,
          href: "/succeedo/george",
          activePaths: ["/succeedo/home"],
          badge: userData?.coaching_conversation?.new_nudge_count,
        },
        { icon: LayoutGrid, label: "Workspace", href: "/succeedo/workspace" },
        ...tools.filter((t) => t.href !== "/succeedo/home").map(nested),
        { icon: CheckSquare, label: "My Action Plan", href: "/succeedo/action-plan", badge: actionPlanBadge },
      ],
    },
    {
      label: "PARTNERS",
      items: [
        { icon: Users, label: "Your Team", href: "/succeedo/teams" },
        { icon: Briefcase, label: "Find a Partner", href: "/succeedo/partners" },
        { icon: MessageSquare, label: "Messages", href: "/succeedo/messages" },
      ],
    },
  ];

  // The phone drawer is never compact, whatever the desktop choice.
  const compact = collapsed && !mobileOpen;

  let sections = ownerSections;
  if (isPartner) sections = partnerSections;
  else if (georgeEnabled && effectivePersona !== "BUYER") sections = georgeSections;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        type="button"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-4 left-4 z-[60] text-muted-foreground hover:text-foreground"
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
      >
        {mobileOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <Menu className="w-5 h-5" />
        )}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-background/50 z-40"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <aside
        id="succeedo-sidebar"
        aria-label="Main navigation"
        className={cn(
          "fixed left-0 top-14 bottom-0 w-[240px] bg-card/80 border-r border-border backdrop-blur-[20px] z-50 flex flex-col transition-[transform,width] duration-300",
          compact && "md:w-[64px]",
          "md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className={cn("flex-1 py-6", compact ? "flex flex-col" : "overflow-y-auto")}>
          <SidebarContent
            sections={sections}
            planProgress={planProgress}
            showPlanProgress={!isPartner && !!planProgress}
            onNavClick={closeMobile}
            compact={compact}
          />
        </div>
      </aside>
    </>
  );
}
